import React, { useState, useEffect } from 'react';
import { X, ScanLine, ChevronRight, Receipt, CreditCard, Ticket, LineChart, Users } from 'lucide-react';
import { BUDDY_HI_IMG } from '../BuddyLogo';
import { hasSeenWelcome, markWelcomeSeen } from '../../utils/onboarding';

const PRIMARY = '#2D5BFF';
const GRADIENT = 'linear-gradient(135deg, #2D5BFF 0%, #1B3DBF 100%)';

/**
 * OnboardingWelcome — a one-time, skippable welcome shown on first login.
 * Self-gates via localStorage; renders nothing once seen.
 */
export default function OnboardingWelcome({ userName = 'there', onScan, onExploreCircles }) {
  const [open, setOpen] = useState(false);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    if (!hasSeenWelcome()) setOpen(true);
  }, []);

  const close = () => {
    markWelcomeSeen();
    setOpen(false);
  };

  if (!open) return null;

  const firstName = (userName || 'there').split(' ')[0];

  const slides = [
    {
      art: (
        <img src={BUDDY_HI_IMG} alt="Buddy" style={{ width: 96, height: 96, objectFit: 'contain' }} />
      ),
      title: `Hi ${firstName}, I'm Buddy 👋`,
      body: "Snap a photo of any receipt and I'll sort out the shop, the items and the total for you — no typing needed.",
    },
    {
      art: (
        <div className="grid grid-cols-2 gap-3">
          {[
            { icon: Receipt, label: 'Receipts', bg: '#EEF2FF', c: PRIMARY },
            { icon: CreditCard, label: 'Loyalty cards', bg: '#ECFDF5', c: '#059669' },
            { icon: Ticket, label: 'Vouchers', bg: '#FFF7ED', c: '#EA580C' },
            { icon: LineChart, label: 'Insights', bg: '#FAF5FF', c: '#9333EA' },
          ].map((t) => (
            <div key={t.label} className="flex items-center gap-2 rounded-2xl" style={{ background: t.bg, padding: '12px 14px' }}>
              <t.icon className="w-5 h-5" style={{ color: t.c }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#111827', fontFamily: 'DM Sans, sans-serif' }}>{t.label}</span>
            </div>
          ))}
        </div>
      ),
      title: 'Everything in one place',
      body: 'Keep your receipts, loyalty cards and vouchers together — and see exactly where your money goes.',
    },
    {
      art: (
        <div className="flex items-center justify-center" style={{ width: 96, height: 96, borderRadius: 28, background: GRADIENT }}>
          <Users className="w-11 h-11" style={{ color: '#FFFFFF' }} />
        </div>
      ),
      title: 'Share with your Circle',
      body: 'Create a Buddy Circle for your family or flatmates and share receipts with each other in a tap.',
    },
  ];

  const isLast = slide === slides.length - 1;
  const current = slides[slide];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(17,24,39,0.55)', backdropFilter: 'blur(4px)' }}
      data-testid="onboarding-welcome"
    >
      <div
        className="w-full sm:max-w-sm bg-white"
        style={{
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          padding: '22px 22px calc(22px + env(safe-area-inset-bottom, 0px)) 22px',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.2)',
        }}
      >
        {/* Skip */}
        <div className="flex justify-end">
          <button onClick={close} data-testid="welcome-skip" className="flex items-center gap-1" style={{ color: '#9CA3AF', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
            Skip <X className="w-4 h-4" />
          </button>
        </div>

        {/* Art */}
        <div className="flex items-center justify-center" style={{ minHeight: 118 }}>
          {current.art}
        </div>

        {/* Copy */}
        <h2 className="mt-4 text-center" style={{ fontSize: 22, fontWeight: 700, color: '#111827', fontFamily: 'Manrope, sans-serif' }}>
          {current.title}
        </h2>
        <p className="mt-2 text-center mx-auto" style={{ fontSize: 15, lineHeight: 1.5, color: '#6B7280', maxWidth: 300, fontFamily: 'DM Sans, sans-serif' }}>
          {current.body}
        </p>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mt-5">
          {slides.map((_, i) => (
            <span key={i} style={{ width: i === slide ? 20 : 7, height: 7, borderRadius: 999, background: i === slide ? PRIMARY : '#E5E7EB', transition: 'all .2s' }} />
          ))}
        </div>

        {/* Actions */}
        <div className="mt-6">
          {isLast ? (
            <div className="flex flex-col gap-2">
              <button
                onClick={() => { close(); onScan && onScan(); }}
                data-testid="welcome-scan"
                className="w-full flex items-center justify-center gap-2 rounded-full"
                style={{ background: GRADIENT, color: '#FFFFFF', padding: '14px 0', fontSize: 16, fontWeight: 700, fontFamily: 'DM Sans, sans-serif', boxShadow: '0 8px 20px rgba(45,91,255,0.35)' }}
              >
                <ScanLine className="w-5 h-5" /> Scan my first receipt
              </button>
              <button
                onClick={close}
                className="w-full rounded-full"
                style={{ color: '#6B7280', padding: '12px 0', fontSize: 15, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}
              >
                Explore on my own
              </button>
            </div>
          ) : (
            <button
              onClick={() => setSlide((s) => s + 1)}
              data-testid="welcome-next"
              className="w-full flex items-center justify-center gap-1 rounded-full"
              style={{ background: GRADIENT, color: '#FFFFFF', padding: '14px 0', fontSize: 16, fontWeight: 700, fontFamily: 'DM Sans, sans-serif' }}
            >
              Next <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
